

import { IconButton } from "@/components/Buttons/IconButton";
import { CoveringLoadingSpinner } from "@/components/LoadingSpinner/CoveringLoadingSpinner";
import { PDeleteConfirmation } from "@/components/PDeleteConfirmation/PDeleteConfirmation";
import { DeleteImpactSummary, impactHasLines } from "@/components/PBulkActionConfirmation/DeleteImpactSummary";
import { trashOf } from "@/lib/guests/trashCopy";
import { isTrashDeleteResult } from "@/types/GuestTrash";
import { armGuestTrashUndo } from "@/lib/guests/guestTrashUndo";
import { fetchGuestDeleteImpact } from "@/lib/api/handlers/GuestHandling";
import { usePortalLinkCopy } from "../../portal-link/usePortalLinkCopy";
import { buildLiveUrl } from "@/lib/website/liveUrl";
import type { GuestDeleteImpact } from "@/types/GuestDeleteImpact";
import { useGuestLogs } from "@/providers/GuestLogs";
import { useGuests } from "@/providers/GuestsProvider";
import { useGuestCompanions } from "@/providers/GuestCompanions";
import { COMPANION_SCOPE } from "@/types/enums/CompanionScope";
import {
  IconArchive,
  IconCheck,
  IconLink,
  IconLoader2,
  IconMail,
  IconMessage,
  IconQrcode,
  IconQrcodeOff,
  IconTrash,
  IconWorld,
} from "@tabler/icons-react";
import { AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { useCallback, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useEventContext } from "@/providers/EventProvider";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import { Plugin } from "@/types/enums/Plugin";
import { useWebsiteSummary } from "@/lib/query/hooks/useWebsiteSummary";
import { Guest } from "@/types/Guest";
import { PSendEmailToGuest } from "@/components/PSendEmailToGuest/PSendEmailToGuest";
import { PCheckInParty } from "@/components/PCheckInParty/PCheckInParty";
import { isPartyMember, opensPartySheet } from "@/lib/guests/party";
import { useSmsManualSend } from "@/lib/sms/smsManualSend";
import { pickSmsNumber } from "@/lib/utils/smsPhone";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { checkInFailureText } from "@/lib/guests/checkInFailureText";
import { lazy, Suspense, useId, useMemo } from "react";
import { useLazyOpen } from "@/hooks/useLazyOpen";
import { useResolvedGuestDetailLayout } from "../../layout/useResolvedGuestDetailLayout";
import {
  ACTION_KEYS,
  resolveSectionConfig,
} from "../../layout/guestDetailLayout";
import type { ActionKey } from "../../layout/guestDetailLayout";
import { useToast } from "@/components/Toast/Toast";
import { describeGuestBulkError } from "@/lib/bulk/describeGuestBulkError";
import { base64UrlUtf8 } from "@/lib/utils/base64url";
const loadGuestQR = () => import("@/components/PGuestQR/PGuestQR");
const PGuestQR = lazy(() => loadGuestQR().then(m => ({ default: m.PGuestQR })));
const loadSendSms = () => import("@/components/PSendSms/PSendSms");
const PSendSms = lazy(() => loadSendSms().then(m => ({ default: m.PSendSms })));
// The portal-link button shows its own spinner instead of IconButton's
// `loading`: that prop also DISABLES the button, and a disabled element drops
// keyboard focus onto <body> — after the copy, nothing would be focused and the
// "Link copied" swap would happen on an element the reader has left. The
// per-guest mint lock (usePortalLinkCopy) is the double-submit guard; the icon
// only reports.
const IconSpinner: React.FC<{ size?: number; className?: string }> = ({ size, className }) => (
  <IconLoader2 size={size} className={`${className ?? ""} animate-spin`} />
);

type ActionsProps = {
  archived: boolean;
  guest: Guest;
  onCloseAction: () => void;
  /**
   * An archive / unarchive from this header LANDED (PB-FB-L22-ARCHIVE). The
   * popup's layout decides on `archived` too (no portal-link cell for an
   * archived guest) and read the list's flag, fixed when the popup opened —
   * the cell stayed on screen after "Archive" until the popup was reopened.
   */
  onArchivedChange?: (archived: boolean) => void;
};

export const Actions: React.FC<ActionsProps> = ({
  archived: archivedProp,
  guest,
  onCloseAction,
  onArchivedChange,
}) => {
  const t = useTranslations("guestDetails");
  // "Ausgecheckt" badge label — same key the guest-table badge uses.
  const tCheckIn = useTranslations("checkIn");
  const tErrors = useTranslations("errors");
  const tBulk = useTranslations("bulkConfirm");
  // The refusal words are the party sheet's, because it is the same write.
  const tParty = useTranslations("checkInParty");
  // "Link copied" is the attachments tab's word — same action, same word.
  const tAttachments = useTranslations("attachments");
  const tSms = useTranslations("smsManualSend");
  const { showToast } = useToast();
  const params = useParams();
  const eventId = params.eventId as string;
  const eventCtx = useEventContext();
  const { event } = eventCtx;
  // Per-action capability gates — a CUSTOM role lands here whenever its legacy
  // floor isn't VIEWER, but it may still lack individual action caps. Gate each
  // button so it never renders an action the role would only get a 403 from.
  const capabilities = useCapabilities();
  const { can, fieldAllowlist } = capabilities;
  // The button below derives its direction from `guest.checkedIn`. A CUSTOM
  // role whose allowlist withholds the field gets it masked — the button would
  // always say "Check in" and could never check anyone OUT. Same rule as the
  // list cell (TCCCheckInOut): no field, no button. `Array.isArray`, not
  // `!== null`: mocks and older hosts carry no allowlist at all.
  const checkInFieldVisible =
    !Array.isArray(fieldAllowlist) || fieldAllowlist.includes("checkedIn");
  // Read-only preview: write actions still render (chrome uses the role's full
  // caps) but are masked by the token — guard the click so it explains rather
  // than 403s with a misleading "no permission" message.
  const { guard } = usePreviewWriteGuard();
  const guestId = guest.guestId!;
  // Tri-state: true = checked in, false = checked OUT (was in, left),
  // null = never scanned — mirrors TCCCheckInStatus in the guest table.
  const [checkedIn, setCheckedIn] = useState<boolean | null>(
    guest.checkedIn ?? null,
  );
  const [archived, setArchivedHere] = useState(archivedProp);
  /** The header's own state AND the popup's (`onArchivedChange`), after a landed write. */
  const setArchived = (value: boolean) => {
    setArchivedHere(value);
    onArchivedChange?.(value);
  };
  const [deletePopup, setDelete] = useState(false);
  // Delete-impact preview for the confirmation (undefined = loading,
  // null = preview failed → plain dialog).
  const [deleteImpact, setDeleteImpact] = useState<GuestDeleteImpact | null | undefined>(undefined);
  // The trash the impact promises — null = today's permanent dialog (an impact
  // without `trash`, i.e. a backend without it, or the flag off). A trash
  // dialog offers no way around the trash (22.10., owner): the permanent
  // delete is the typed purge on the trash page or the activity row.
  const trashMode = trashOf(deleteImpact) !== null;
  // Audit-B M1: before the impact lands the sentence promises neither
  // "permanent" nor "trash" and the confirm waits; an impact that FAILED
  // keeps a neutral sentence and lets the user go on.
  const impactPending = deleteImpact === undefined;
  const impactFailed = deleteImpact === null;
  const [sendEmailPopup, setSendEmailPopup] = useState(false);
  const [qrPopup, setQrPopup] = useState(false);
  const { opening: openingQr, open: openQr } = useLazyOpen(loadGuestQR);
  const [sendSmsPopup, setSendSmsPopup] = useState(false);
  const { opening: openingSms, open: openSms } = useLazyOpen(loadSendSms);
  // SMS manual send (F-2): the same gate as the guest table's "Send SMS",
  // and only for a guest on the active list — the only guests a manual send
  // reaches. Without consent or a number that can get SMS the button stays,
  // soft-disabled, and says why (F3-14); the dialog's check is the authority.
  const canSendSms = useSmsManualSend(eventId, { caps: capabilities, ctx: eventCtx });
  const smsReasonId = useId();
  const smsSelection = useMemo(() => ({ mode: "ids" as const, ids: [guestId] }), [guestId]);
  const smsBlockedReason =
    guest.smsOptIn !== true
      ? tSms("drawer.noConsent")
      : (() => {
          const verdict = pickSmsNumber(guest.mobile, guest.phone).verdict;
          return verdict === "noNumber"
            ? tSms("drawer.noNumber")
            : verdict === "unreachableNumber"
              ? tSms("drawer.unreachableNumber")
              : null;
        })();
  // "Copy portal link": mints a personal portal token for this guest and puts
  // the URL on the clipboard. Lives in the header because the attachments tab
  // only offers it when the guest has LINK attachments — an event that uses
  // the portal for the ticket alone (0 attachments) had no way to hand out a
  // link per guest except the bulk export. The flow (clipboard write started
  // inside the click for WebKit, 30 s ceiling, the honest toasts) lives in
  // usePortalLinkCopy, shared with the layout's portal-link cell (W3-3) — and
  // so is the busy lock: ONE per guest across both buttons, so a click on each
  // inside one tick mints once.
  const {
    state: portalLinkState,
    busy: portalLinkBusy,
    copy: copyPortalLink,
  } = usePortalLinkCopy(eventId, guest.guestId, { resetAfterMs: 2000 });
  const hasAttachmentsPlugin = event?.plugins?.includes(Plugin.ATTACHMENTS);
  // A ref as well as state (`TCCAction.tsx:64-66`): two clicks inside one
  // tick read the same `loading` value, so state alone cannot stop a double
  // submit — and neither can the `disabled` attribute it drives.
  const submitRef = useRef(false);
  const [loading, setLoading] = useState(false);
  // Shown inside whichever confirmation dialog is open. The two dialog-less
  // buttons have no surface for it and use a toast instead.
  const [actionError, setActionError] = useState<string | undefined>();
  // P3 (UX1): confirm dialog before archiving a mainGuest with companions,
  // since the backend cascades the archive to them all. Without the
  // warning the admin can't tell companions came along.
  const [archivePopup, setArchivePopup] = useState<{ mode: "archive" | "unarchive" } | null>(null);
  // Check-in with companions: same sheet as the guest-list row action.
  const [partyOpen, setPartyOpen] = useState(false);
  /** How many companions the pending archive/unarchive confirmation is about. */
  const [cascadeCount, setCascadeCount] = useState<number | null>(0);
  const { peekCompanions } = useGuestCompanions();
  /**
   * How many companions travel with this host on an archive/unarchive.
   *
   * Asked for at the moment of the decision, in the scope this decision means
   * (`ALL` — an archived host's companions are archived too, and they are
   * exactly the ones an unarchive brings back). It used to be read off the
   * provider's shared map, whose contents depend on which tab the operator
   * happened to open; for an archived host that map holds nothing in either
   * party scope, so the confirmation stopped opening in the one case it exists
   * for. Companions have no party of their own, so they never ask.
   *
   * ⚠ Returns `null` for "could not find out", and the caller warns on it.
   * Reading a failure as "solo guest" would archive somebody's whole party
   * with no confirmation because the network blipped — an empty read is never
   * the permissive answer here.
   *
   * `peekCompanions`, not `fetchCompanions`: the failure has to come back in
   * the same return value (React state cannot reach this closure after the
   * await), and this read must not push its wider row set into the panel's
   * shared list, whose dropdown says something narrower.
   */
  const countCompanionsForCascade = useCallback(async (): Promise<number | null> => {
    if (guest.guestType !== "mainGuest" || !guest.guestId) return 0;
    const result = await peekCompanions(guest.guestId, COMPANION_SCOPE.ALL);
    if ("failed" in result) return null;
    return result.rows.length;
  }, [peekCompanions, guest.guestId, guest.guestType]);
  const { checkInGuests, archiveGuests, unarchiveGuests, deleteGuests } =
    useGuests();
  const { fetchLogs } = useGuestLogs();

  const hasWebsitePlugin = event?.plugins?.includes(Plugin.WEBSITE);
  const hasMessagingPlugin = event?.plugins?.includes(Plugin.MESSAGING);

  // useWebsiteSummary caches the result per-event for 5 min so the N drawer
  // remounts you get from clicking through the guestlist don't fire N GETs.
  // See plan ~/.claude/plans/floofy-leaping-pascal.md for the full context.
  const { websiteVisible, customDomain } = useWebsiteSummary(
    eventId,
    !!hasWebsitePlugin,
  );
  // buildLiveUrl is the single source of truth (audit L1: this inline
  // copy still pointed at the never-existing landingpages-plus bucket).
  const websiteUrl = websiteVisible ? buildLiveUrl(eventId, customDomain) : null;
  // The SAME url with this guest's credentials attached — null when they have
  // none, which is the honest answer and not the same thing as "the website".
  //
  // Kept apart since 2026-08-16: the "visit website" button is happy with the
  // plain event URL, but the QR code offers a mode that calls itself the
  // guest's PERSONALISED link (`qrWebsiteDescription`). Sharing one value made
  // that mode appear for credential-less guests and encode the public
  // homepage — a QR the organiser hands over believing it logs the guest in.
  // Every lane mints credentials now, so this is mostly a guard for rows that
  // predate that; it still has to be a guard rather than an assumption.
  //
  // base64url, matching main-consumer's `base64UrlUtf8` — NOT bare `btoa`,
  // which is what this line used until 2026-08-21. Two concrete defects came
  // out of that mismatch:
  //   - `btoa` emits `+`, and `+` in a query string decodes to a space. Every
  //     reader compensates with `.replace(/ /g, "+")`; there are three such
  //     copies, one of them in a customer-authored site we do not control.
  //   - `btoa` encodes Latin-1 and throws above U+00FF, so a username with an
  //     umlaut produced different bytes here than in the emailed link for the
  //     same guest.
  // The QR code below is fed from this value and gets printed on badges, so a
  // wrong link here is not something a redeploy fixes.
  const personalWebsiteUrl =
    websiteUrl && guest.username && guest.code
      ? `${websiteUrl}?credentials=${base64UrlUtf8(guest.username + ":" + guest.code)}`
      : null;

  // Order/visibility from the SELECTED layout (view-linked / user-picked /
  // Standard — same resolution as the field groups and sidebar tabs, so the
  // whole popup follows ONE layout). Applies to the NON-archived action row
  // only — the archived view (unarchive) is never configurable, or a hidden
  // "archive" key could strand a guest archived. Capability/plugin gates
  // below stay the final authority.
  const { layout: selectedLayout, isPending: layoutPending } =
    useResolvedGuestDetailLayout(eventId);
  const actionsConfig = useMemo(
    () => resolveSectionConfig(selectedLayout?.actions, ACTION_KEYS, { missing: "slot" }),
    [selectedLayout?.actions],
  );
  const actionVisible = (key: ActionKey) => !actionsConfig.hidden.has(key);
  const actionOrder = (key: ActionKey) => actionsConfig.order.indexOf(key);

  // Don't paint the DEFAULT order while the config is in flight — buttons
  // would visibly reshuffle/disappear when it lands.
  if (eventId && layoutPending) return null;

  return (
    <div className="flex flex-row flex-wrap gap-3 items-center">
      {!archived ? (
        <>
          {actionVisible("checkIn") && can("guests:check-in") && checkInFieldVisible && (
          <button
            style={{ order: actionOrder("checkIn") }}
            className={`flex flex-row items-center rounded-lg border outline-quaternary surface-primary ${
              loading ? "opacity-50 cursor-default" : "cursor-pointer hover:surface-secondary"
            }`}
            onClick={guard(async () => {
              if (submitRef.current) return;
              // Companions come along — but only if the operator says so. Same
              // sheet, and the same GATE, as the guest-list row: `opensPartySheet`
              // reads the guest row itself (`numCompanions` is in the drawer's
              // projection) rather than the companions cache, which is only
              // filled once someone opened the companions tab — AND requires the
              // capability the sheet's own load needs, so a check-in-only role
              // keeps the one-click write it has always had.
              if (opensPartySheet(guest, can)) {
                setPartyOpen(true);
                return;
              }
              // Taken only on the path that actually writes: the party-sheet
              // branch above returns early and never reaches the `finally`.
              submitRef.current = true;
              setLoading(true);
              try {
                // ONE lane for all three doors. The old one funnelled every
                // failure through `handleErrorServer`, which keeps `message`
                // and drops the body — and this endpoint's 409 carries its
                // reason in `error`, with no `errorCode` and no `message`. It
                // also derived the direction from the provider's TOP-LEVEL
                // rows, which are `[]` on the requests page and miss a nested
                // companion, so "check out" sent `checkIn: true`.
                //
                // `checkedIn !== true` rather than `!checkedIn`: same
                // expression the party sheet is handed below, and the state is
                // tri-state.
                const report = await checkInGuests([guestId], checkedIn !== true);
                const result = report.results[0];
                await fetchLogs(guestId, true);
                // No `checkInActionCompleted` here: `checkInGuests` dispatches
                // it itself, and conditionally — dispatching again would just
                // buy a second `refetchOpenGuest`.
                if (!result || result.status === "failed") {
                  // Dialog-less button ⇒ toast (see `actionError` above).
                  showToast(
                    checkInFailureText(result?.error, {
                      checkIn: tCheckIn,
                      errors: tErrors,
                      party: tParty,
                    }),
                    "error",
                  );
                } else {
                  if (result.warning === "capacity") {
                    // WARNING mode: the guest IS in, over the limit. In the red
                    // the refusal arm above uses, that reads as "nothing
                    // happened" — and the next click is a check-OUT.
                    showToast(tCheckIn("capacityExceeded"), "info");
                  }
                  setCheckedIn(checkedIn !== true);
                }
              } catch {
                // `runPartyCheckIn` books refusals in the report rather than
                // throwing; anything still landing here is the transport
                // giving out — and `guard()` does NOT catch, so without this
                // the rejection escaped and `loading` stayed true forever.
                showToast(tErrors("unknown"), "error");
              } finally {
                submitRef.current = false;
                setLoading(false);
              }
            })}
            disabled={loading}
          >
            <span
              className={`flex items-center gap-1.5 px-2.5 py-2 body-4-medium rounded-l-[7px] self-stretch ${
                checkedIn
                  ? "chip-green-bg chip-green-fg"
                  : checkedIn === false
                    ? "chip-blue-bg chip-blue-fg"
                    : "surface-tertiary font-tertiary"
              }`}
            >
              <span
                className="h-1.5 w-1.5 rounded-full shrink-0"
                style={{
                  backgroundColor: checkedIn
                    ? "var(--color-font-success)"
                    : checkedIn === false
                      ? "var(--color-font-blue)"
                      : "var(--color-font-quaternary)",
                }}
              />
              {/* The status WORD is the widest part of the widest action; the
                  coloured dot beside it already carries the state, so on a
                  phone the word gives way and the pill keeps its meaning. */}
              <span className="hidden sm:inline">
                {checkedIn
                  ? t("statusCheckedIn")
                  : checkedIn === false
                    ? tCheckIn("actionCheckOut")
                    : t("statusNotCheckedIn")}
              </span>
            </span>
            <span
              className="w-px self-stretch"
              style={{ backgroundColor: "var(--color-outline-quaternary)" }}
            />
            <span className="flex items-center gap-1.5 px-2.5 py-2 body-2-medium font-primary">
              {checkedIn ? (
                <IconQrcodeOff size={16} className="font-primary shrink-0" />
              ) : (
                <IconQrcode size={16} className="font-primary shrink-0" />
              )}
              {checkedIn ? t("checkOut") : t("checkIn")}
            </span>
          </button>
          )}
          {actionVisible("sendEmail") && hasMessagingPlugin && guest.email?.trim() && can("sent-emails:send") && (
            <div style={{ order: actionOrder("sendEmail") }}>
            <IconButton
              icon={IconMail}
              width="w-max"
              hideLabelBelowSm
              backgroundColor="surface-primary"
              padding="px-3 py-2"
              text={t("sendEmail")}
              space="gap-1.5"
              color="font-primary"
              iconSize={16}
              border="border outline-quaternary"
              onClickAction={guard(() => setSendEmailPopup(true))}
            />
            </div>
          )}
          {actionVisible("sendSms") && canSendSms && isPartyMember({ ...guest, archived }) && (
            <div style={{ order: actionOrder("sendSms") }}>
              <HoverInfo message={smsBlockedReason ?? undefined} className="inline-flex">
                <IconButton
                  icon={IconMessage}
                  width="w-max"
                  hideLabelBelowSm
                  backgroundColor="surface-primary"
                  padding="px-3 py-2"
                  text={t("sendSms")}
                  space="gap-1.5"
                  color="font-primary"
                  iconSize={16}
                  border="border outline-quaternary"
                  testId="guest-detail-send-sms"
                  loading={openingSms}
                  softDisabled={smsBlockedReason !== null}
                  ariaDescribedBy={smsBlockedReason !== null ? smsReasonId : undefined}
                  onClickAction={guard(() => openSms(() => setSendSmsPopup(true)))}
                />
                {smsBlockedReason !== null && (
                  <span id={smsReasonId} hidden data-testid="guest-detail-send-sms-reason">
                    {smsBlockedReason}
                  </span>
                )}
              </HoverInfo>
            </div>
          )}
          {actionVisible("website") && websiteVisible && (
            <div style={{ order: actionOrder("website") }}>
            <IconButton
              icon={IconWorld}
              width="w-max"
              hideLabelBelowSm
              backgroundColor="surface-primary"
              padding="px-3 py-2"
              text={t("visitWebsite")}
              space="gap-1.5"
              color="font-primary"
              iconSize={16}
              border="border outline-quaternary"
              onClickAction={() => {
                // Prefer the guest's own link; fall back to the plain event
                // site so the button still does something for a row that has
                // no credentials.
                const target = personalWebsiteUrl ?? websiteUrl;
                if (target) window.open(target, "_blank");
              }}
            />
            </div>
          )}
          {actionVisible("qrCode") && guest.guestId && can("guests:read.qr-token") && (
            <div style={{ order: actionOrder("qrCode") }}>
            <IconButton
              icon={IconQrcode}
              width="w-max"
              hideLabelBelowSm
              backgroundColor="surface-primary"
              padding="px-3 py-2"
              text={t("showQrCode")}
              space="gap-1.5"
              color="font-primary"
              iconSize={16}
              border="border outline-quaternary"
              loading={openingQr}
              onClickAction={() => openQr(() => setQrPopup(true))}
            />
            </div>
          )}
          {/* UI gate, mirroring the attachments tab: `attachments:read` as
              well as the mint right, because the link shows the guest's
              attachments — and `guests:read.qr-token`, because for a
              ticket-only event the portal's main content IS the guest's
              scan token (the ticket block renders the QR; the "Show QR
              code" button next door is gated on exactly this). The layout's
              portal-link cell (PortalLinkLayoutCell) carries the same gate.
              The server mints on `shared-links:write` AND
              `guests:read.qr-token` (PB-PQT-1: without the QR right it
              answers 403 `insufficientCapability` before any row is written
              — the tab's copy button, the export column and the AI tool go
              through the same endpoint) and, since W3-3 (PB-PQT-2), only
              for a guest the caller can see: an unknown guest, one of
              another event or one outside the caller's row filter answers
              404 `guestNotFound` and mints nothing. So this gate mirrors
              the server's; the server is the lock. */}
          {actionVisible("portalLink") && hasAttachmentsPlugin && guest.guestId && can("shared-links:write") && can("attachments:read") && can("guests:read.qr-token") && (
            <div style={{ order: actionOrder("portalLink") }}>
            <IconButton
              icon={
                portalLinkState === "copied"
                  ? IconCheck
                  : portalLinkBusy
                    ? IconSpinner
                    : IconLink
              }
              width="w-max"
              hideLabelBelowSm
              backgroundColor="surface-primary"
              padding="px-3 py-2"
              text={
                portalLinkState === "copied"
                  ? tAttachments("linkCopied")
                  : t("copyPortalLink")
              }
              space="gap-1.5"
              color="font-primary"
              iconSize={16}
              border="border outline-quaternary"
              testId="guest-detail-copy-portal-link"
              // The lock is per guest and shared with the layout cell: busy
              // while either one mints (audit r2 L2).
              ariaBusy={portalLinkBusy}
              onClickAction={guard(copyPortalLink)}
            />
            </div>
          )}
        </>
      ) : archived && can("guests:write") ? (
        <IconButton
          icon={IconArchive}
          width="w-max"
          hideLabelBelowSm
          backgroundColor="surface-primary"
          padding="px-3 py-2"
          text={t("unarchive")}
          space="gap-1.5"
          color="font-primary"
          iconSize={16}
          border="border outline-quaternary"
          onClickAction={guard(async () => {
            // P3 (UX1): mainGuest with companions → confirm before
            // unarchive so the admin understands companions come back too.
            const cascadeCount = await countCompanionsForCascade();
            if (cascadeCount === null || cascadeCount > 0) {
              setCascadeCount(cascadeCount);
              setArchivePopup({ mode: "unarchive" });
              return;
            }
            setLoading(true);
            try {
              await unarchiveGuests({ mode: "ids", ids: [guestId] });
              setArchived(false);
            } catch (err) {
              showToast(describeGuestBulkError(err, { tErrors, tBulk }), "error");
            } finally {
              setLoading(false);
            }
          })}
        />
      ) : null}
      {!archived && actionVisible("archive") && can("guests:write") && (
        <div style={{ order: actionOrder("archive") }}>
        <IconButton
          icon={IconArchive}
          width="w-max"
          hideLabelBelowSm
          backgroundColor="surface-primary"
          iconSize={16}
          padding="px-3 py-2"
          text={t("archive")}
          space="gap-1.5"
          color="font-error"
          border="border outline-quaternary"
          onClickAction={guard(async () => {
            // P3 (UX1): mainGuest with companions → confirm before
            // archive. Backend cascades; the admin shouldn't be
            // surprised to find N companions archived in bulk.
            const cascadeCount = await countCompanionsForCascade();
            if (cascadeCount === null || cascadeCount > 0) {
              setCascadeCount(cascadeCount);
              setArchivePopup({ mode: "archive" });
              return;
            }
            setLoading(true);
            try {
              await archiveGuests({ mode: "ids", ids: [guestId] });
              setArchived(true);
            } catch (err) {
              showToast(describeGuestBulkError(err, { tErrors, tBulk }), "error");
            } finally {
              setLoading(false);
            }
          })}
        />
        </div>
      )}
      {/* Config applies to the live row only — an ARCHIVED guest always keeps
          Delete (like Unarchive), or a hidden key would strand it. */}
      {(archived || actionVisible("delete")) && can("guests:delete") && (
        <div style={{ order: actionOrder("delete") }}>
        <IconButton
          icon={IconTrash}
          width="w-max"
          hideLabelBelowSm
          backgroundColor="surface-primary"
          iconSize={16}
          padding="px-3 py-2"
          text={t("delete")}
          space="gap-1.5"
          color="font-error"
          border="border outline-quaternary"
          onClickAction={guard(() => {
            setDelete(true);
            // Best-effort consequence preview (companion cascade, open
            // request, checked-in) — null on failure keeps the plain dialog.
            setDeleteImpact(undefined);
            void fetchGuestDeleteImpact(eventId, { mode: "ids", ids: [guestId] })
              .then(setDeleteImpact);
          })}
        />
        </div>
      )}
      {loading && <CoveringLoadingSpinner />}
      <AnimatePresence>
        {deletePopup && (
          <PDeleteConfirmation
            title={trashMode ? t("trashConfirmationGuest") : t("deleteConfirmationGuest")}
            // Trash copy only when the impact says the backend will trash;
            // otherwise exactly today's sentence (rollback consistency). The
            // trash sentence is the bulk dialog's plain question for one guest —
            // the retention is not spelled out (owner, 22.10.).
            message={
              impactPending
                ? tBulk("impactPending")
                : impactFailed
                  ? tBulk("impactUnavailableGuest")
                  : trashMode
                    ? tBulk("trashQuestion", { count: 1 })
                    : t("deleteConfirmationGuestMessage")
            }
            liveMessage
            confirmDisabled={impactPending}
            messageTestId={trashMode ? "guest-trash-confirm-copy" : undefined}
            primaryButtonText={trashMode ? t("moveToTrash") : undefined}
            onCloseAction={() => {
              setDelete(false);
            }}
            onConfirmAction={async () => {
              setLoading(true);
              setActionError(undefined);
              try {
                const result = await deleteGuests({ mode: "ids", ids: [guestId] });
                if (isTrashDeleteResult(result)) {
                  armGuestTrashUndo({
                    eventId,
                    batchId: result.trashBatchId,
                    count: result.count,
                    purgeAt: result.purgeAt ?? null,
                  });
                }
                setDelete(false);
                onCloseAction();
              } catch (err) {
                // Without this the rejection escaped, `loading` stayed true
                // forever and CoveringLoadingSpinner sat over the whole drawer
                // with no message and no way out. The catch has to be INSIDE
                // the guard() callback — guard discards the promise it wraps.
                setActionError(describeGuestBulkError(err, { tErrors, tBulk }));
              } finally {
                setLoading(false);
              }
            }}
            isLoading={loading}
            error={actionError}
          >
            {deleteImpact && (!trashMode || impactHasLines(deleteImpact)) ? (
              <DeleteImpactSummary impact={deleteImpact} trash={trashMode} />
            ) : null}
          </PDeleteConfirmation>
        )}
      </AnimatePresence>
      {/* P3 (UX1): mainGuest archive/unarchive cascade warning. */}
      <AnimatePresence>
        {archivePopup && (
          <PDeleteConfirmation
            title={archivePopup.mode === "archive"
              ? t("archiveWithCompanionsTitle")
              : t("unarchiveWithCompanionsTitle")}
            message={
              // `null` means the companion read failed, so the count is not
              // known. Saying "0 companions" there would be a confident false
              // statement in exactly the case the confirmation exists for.
              cascadeCount === null
                ? t(
                    archivePopup.mode === "archive"
                      ? "archiveWithCompanionsUnknownMessage"
                      : "unarchiveWithCompanionsUnknownMessage",
                  )
                : t(
                    archivePopup.mode === "archive"
                      ? "archiveWithCompanionsMessage"
                      : "unarchiveWithCompanionsMessage",
                    { count: cascadeCount },
                  )
            }
            onCloseAction={() => {
              setArchivePopup(null);
              setActionError(undefined);
            }}
            onConfirmAction={async () => {
              setLoading(true);
              setActionError(undefined);
              try {
                if (archivePopup.mode === "archive") {
                  await archiveGuests({ mode: "ids", ids: [guestId] });
                  setArchived(true);
                } else {
                  await unarchiveGuests({ mode: "ids", ids: [guestId] });
                  setArchived(false);
                }
                setArchivePopup(null);
              } catch (err) {
                setActionError(describeGuestBulkError(err, { tErrors, tBulk }));
              } finally {
                setLoading(false);
              }
            }}
            isLoading={loading}
            error={actionError}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {partyOpen && (
          <PCheckInParty
            eventId={eventId}
            row={guest}
            // ONE direction for the party — the one this button offered.
            checkIn={checkedIn !== true}
            onClose={() => setPartyOpen(false)}
            onWrote={(writtenIds, didCheckIn) => {
              // The drawer renders `checkedIn` from its own state, which the
              // list's row patch cannot reach.
              if (writtenIds.includes(guestId)) setCheckedIn(didCheckIn);
              void fetchLogs(guestId, true);
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {sendEmailPopup && (
          <PSendEmailToGuest
            onClose={() => setSendEmailPopup(false)}
            guest={guest}
            eventId={eventId}
            eventName={event?.name ?? ""}
          />
        )}
      </AnimatePresence>
      <Suspense fallback={null}>
        <AnimatePresence>
          {sendSmsPopup && (
            <PSendSms
              eventId={eventId}
              eventName={event?.name ?? ""}
              // The drawer's guest and nobody else — ticked rows in the
              // table behind it do not come along.
              selection={smsSelection}
              guest={guest}
              onClose={() => setSendSmsPopup(false)}
            />
          )}
        </AnimatePresence>
      </Suspense>
      {/* Local Suspense so first-load chunk doesn't bubble up to App.tsx covering spinner. */}
      <AnimatePresence>
        {qrPopup && (
          <Suspense fallback={null}>
            <PGuestQR
              guest={guest}
              eventId={eventId}
              websiteUrl={personalWebsiteUrl}
              onClose={() => setQrPopup(false)}
            />
          </Suspense>
        )}
      </AnimatePresence>
    </div>
  );
};
